// apps/alivia/exam-blueprint.js — BP·1: HER CLEP Introductory Psychology blueprint, REAL DATA
// transcribed from the official CLEP Introductory Psychology examination guide (replaces run 6's
// placeholder). Thirteen domains with the official percentage RANGES; the weight is the range
// MIDPOINT, normalized so the thirteen sum to exactly 100 (midpoints sum to 99.5 — the
// midpoint-normalization lean is recorded in the decisions log). The exam adheres to DSM-5
// terminology. Tag keys match her deck's 13 domain tags (tag-aliases.js).
export const BLUEPRINT_PLACEHOLDER = false; // real data — the run-6 placeholder is gone

/** The official domain table: tag → { name, low, high, mid, subtopics (documentation only) }. */
export const DOMAIN_TABLE = {
  history: {
    name: "History, Approaches, Methods", low: 11, high: 12, mid: 11.5,
    subtopics: ["history of psychology", "approaches (biological, behavioral, cognitive, humanistic, psychodynamic, evolutionary)", "research methods (experimental, correlational, clinical)", "ethics in research"],
  },
  social: {
    name: "Social Psychology", low: 9, high: 10, mid: 9.5,
    subtopics: ["group dynamics", "attribution processes", "interpersonal perception", "conformity, compliance, obedience", "attitudes and attitude change", "aggression / antisocial behavior"],
  },
  biology: {
    name: "Biological Bases of Behavior", low: 8, high: 9, mid: 8.5,
    subtopics: ["endocrine system", "etiology (genetics)", "functional organization of the nervous system", "neuroanatomy", "physiological techniques"],
  },
  learning: {
    name: "Learning", low: 8, high: 9, mid: 8.5,
    subtopics: ["classical conditioning", "operant conditioning", "observational learning", "biological bases of learning", "cognitive processes in learning"],
  },
  cognition: {
    name: "Cognition", low: 8, high: 9, mid: 8.5,
    subtopics: ["memory", "language", "thinking and problem solving", "intelligence and creativity"],
  },
  development: {
    name: "Developmental Psychology Across the Lifespan", low: 8, high: 9, mid: 8.5,
    subtopics: ["theories of development", "dimensions of development (physical, cognitive, social, moral)", "gender identity and sex roles", "research methods (longitudinal, cross-sectional)", "heredity–environment issues"],
  },
  disorders: {
    name: "Psychological Disorders and Health", low: 8, high: 9, mid: 8.5,
    subtopics: ["affective disorders", "anxiety disorders", "dissociative disorders", "personality disorders", "psychotic disorders", "somatoform disorders", "health, stress and coping (DSM-5 terminology)"],
  },
  sensation: {
    name: "Sensation and Perception", low: 7, high: 8, mid: 7.5,
    subtopics: ["receptor processes (vision, audition)", "sensory mechanisms and thresholds", "perceptual development", "perceptual processes", "attention"],
  },
  personality: {
    name: "Personality", low: 7, high: 8, mid: 7.5,
    subtopics: ["personality theories and approaches", "assessment techniques", "research methods (idiographic, nomothetic)", "self-concept, self-esteem"],
  },
  treatment: {
    name: "Treatment of Psychological Disorders", low: 6, high: 7, mid: 6.5,
    subtopics: ["insight therapies", "behavioral therapies", "cognitive therapies", "biological therapies", "community and preventive approaches"],
  },
  consciousness: {
    name: "States of Consciousness", low: 5, high: 6, mid: 5.5,
    subtopics: ["sleep and dreaming", "hypnosis and meditation", "psychoactive drug effects"],
  },
  motivation: {
    name: "Motivation and Emotion", low: 5, high: 6, mid: 5.5,
    subtopics: ["biological bases", "theories of motivation", "hunger, thirst, sex, pain", "social motivation", "theories of emotion"],
  },
  stats: {
    name: "Statistics, Tests and Measurement", low: 3, high: 4, mid: 3.5,
    subtopics: ["descriptive statistics", "measurement of intelligence", "reliability and validity", "samples, populations, norms", "types of tests"],
  },
};

const MID_SUM = Object.values(DOMAIN_TABLE).reduce((s, d) => s + d.mid, 0); // 99.5 by the guide's own ranges

/** {tag: weight} — the range midpoints normalized to sum to exactly 100. */
export const BLUEPRINT = Object.fromEntries(
  Object.entries(DOMAIN_TABLE).map(([tag, d]) => [tag, (d.mid * 100) / MID_SUM]),
);

// The official form: ~95 questions in 90 minutes → ~57s per question drives the sim's pace marker.
export const EXAM_TOTAL_QUESTIONS = 95;
export const EXAM_MINUTES = 90;
export const SECONDS_PER_QUESTION = Math.round((EXAM_MINUTES * 60) / EXAM_TOTAL_QUESTIONS);

// ── BP·2 — tag → official-domain mapping (client-safe; the node reporter imports it too) ────────
// Authored from the tag names ACTUALLY PRESENT in her deck (the 13 domain tags — the seeded deck
// and tag-aliases normalization mean tags already ARE the official domains, so the table is
// identity today; zero REVIEW entries were needed). Any FUTURE tag not in this table is UNMAPPED:
// the sim excludes it from the draw and REPORTS the exclusion — never a silent drop.
export const TAG_TO_DOMAIN = Object.fromEntries(Object.keys(DOMAIN_TABLE).map((t) => [t, t]));

/**
 * Map a bank's items onto official domains for the blueprint draw. An item keeps only its mapped
 * domain tags; an item with NO mapped tag is excluded and returned separately (the manifest names
 * the exclusion).
 * @param {Array<{id: string, tags?: string[]}>} items
 * @returns {{ bank: Array<object>, excluded: Array<object> }}
 */
export function mapBankToDomains(items) {
  const bank = [];
  const excluded = [];
  for (const q of items ?? []) {
    const domains = [...new Set((q.tags ?? []).map((t) => TAG_TO_DOMAIN[t]).filter(Boolean))];
    if (domains.length === 0) excluded.push(q);
    else bank.push({ ...q, tags: domains });
  }
  return { bank, excluded };
}
